export const MATCH_DURATION_MS = 60_000;

export function matchDurationMs(bundle) {
  return Math.max(MATCH_DURATION_MS, Number(bundle?.duration_ms) || 0);
}

function questionIndexes(state) {
  if (state?.questionIndex) return state.questionIndex;
  return ['a', 'b'].reduce((indexes, side) => ({
    ...indexes,
    [side]: state?.attempts?.filter((attempt) => attempt.side === side && attempt.phase === 'normal').length || 0,
  }), {});
}

function questionFromOrder(bundle, side, displayedIndex) {
  const order = bundle?.question_orders?.[side];
  const questionIndex = Array.isArray(order) && order.length === bundle?.questions?.length
    ? order[displayedIndex]
    : displayedIndex;
  return bundle?.questions?.[questionIndex];
}

export function createLocalMatch(match, now = Date.now()) {
  const bundle = match?.question_bundle;
  if (!bundle?.questions?.length || !bundle?.golden_question || !bundle?.duration_ms) {
    throw new Error('Karşılaşma soruları yüklenemedi.');
  }
  return {
    matchId: match.id,
    startedAt: now,
    phase: 'normal',
    progress: { a: 0, b: 0 },
    questionIndex: { a: 0, b: 0 },
    attempts: [],
    winnerSide: null,
  };
}

export function elapsedLocalMs(state, now = Date.now()) {
  const previous = state.attempts[state.attempts.length - 1]?.elapsed_ms || 0;
  return Math.max(previous, Math.max(0, now - state.startedAt));
}

export function tickLocalMatch(state, bundle, now = Date.now()) {
  if (!state || state.phase !== 'normal' || elapsedLocalMs(state, now) < matchDurationMs(bundle)) return state;
  const { a, b } = state.progress;
  return {
    ...state,
    phase: a === b ? 'golden' : 'completed',
    winnerSide: a === b ? null : a > b ? 'a' : 'b',
  };
}

export function questionForSide(state, bundle, side) {
  if (!state || state.phase === 'completed') return null;
  const index = state.phase === 'golden' ? 0 : questionIndexes(state)[side];
  const question = state.phase === 'golden' ? bundle.golden_question : questionFromOrder(bundle, side, index);
  if (!question) return null;
  return {
    phase: state.phase,
    index,
    prompt: question.prompt,
    dialogue: question.dialogue,
    choices: [...question.choices],
    image_url: question.image_url,
    choice_media: question.choice_media,
  };
}

export function answerLocalMatch(state, bundle, side, choice, now = Date.now()) {
  const current = tickLocalMatch(state, bundle, now);
  if (!current || current.phase !== state.phase || current.phase === 'completed' || !['a', 'b'].includes(side)) {
    return { state: current, feedback: null };
  }
  const question = questionForSide(current, bundle, side);
  if (!question || !question.choices.includes(choice)) return { state: current, feedback: null };
  const elapsed = elapsedLocalMs(current, now);
  const correctQuestion = current.phase === 'golden'
    ? bundle.golden_question
    : questionFromOrder(bundle, side, question.index);
  const correct = choice === correctQuestion.correct_answer;
  const attempt = {
    side, phase: question.phase, question_index: question.index,
    selected_answer: choice, elapsed_ms: elapsed,
  };
  const next = {
    ...current,
    attempts: [...current.attempts, attempt],
    progress: { ...current.progress },
    questionIndex: { ...questionIndexes(current) },
  };
  if (current.phase === 'normal') {
    next.questionIndex[side] += 1;
    if (correct) next.progress[side] += 1;
    if (next.progress[side] === bundle.questions.length) {
      next.phase = 'completed';
      next.winnerSide = side;
    } else if (Object.values(next.questionIndex).every((index) => index >= bundle.questions.length)) {
      if (next.progress.a === next.progress.b) {
        next.phase = 'golden';
      } else {
        next.phase = 'completed';
        next.winnerSide = next.progress.a > next.progress.b ? 'a' : 'b';
      }
    }
  } else if (current.phase === 'golden' && correct) {
    next.phase = 'completed';
    next.winnerSide = side;
  }
  return { state: next, feedback: { phase: question.phase, side, choice, correct, question } };
}

export function localMatchStorageKey(token, tournamentId, matchId) {
  return `mini-task-tournament:${token}:${tournamentId}:${matchId}`;
}

export function localTournamentStorageKey(token) {
  return `mini-task-tournament-session:${token}`;
}

export function startLocalTournamentMatch(tournament, matchId) {
  return {
    ...tournament,
    matches: tournament.matches.map((match) => match.id === matchId ? { ...match, status: 'active' } : match),
  };
}

export function advanceLocalTournament(tournament, matchId, winnerSide) {
  const matches = tournament.matches.map((match) => ({ ...match }));
  const match = matches.find((item) => item.id === matchId);
  if (!match || match.status !== 'active' || !['a', 'b'].includes(winnerSide)) {
    throw new Error('Eşleşme sonucu geçersiz.');
  }
  const winner = match[`student_${winnerSide}`];
  match.winner = winner;
  match.status = 'completed';
  const next = matches.find((item) => item.round === match.round + 1 && item.slot === Math.floor(match.slot / 2));
  if (!next) return { ...tournament, status: 'completed', champion: winner, matches };
  next[match.slot % 2 === 0 ? 'student_a' : 'student_b'] = winner;
  if (next.student_a && next.student_b) {
    const variants = next.pair_variants;
    if (variants) {
      const key = variants[`${next.student_a.id}:${next.student_b.id}`];
      const variant = next.variant_bundles?.[key];
      if (!variant?.question_bundle) throw new Error('Eşleşme soruları hazırlanamadı.');
      next.question_bundle = variant.question_bundle;
      next.activity = variant.activity;
    }
    next.status = 'ready';
  }
  return { ...tournament, matches };
}

export async function preloadTournamentImages(bundle) {
  const urls = new Set();
  const collect = (value) => {
    if (!value || typeof value !== 'object') return;
    Object.entries(value).forEach(([key, item]) => {
      if (['image_url', 'imageUrl', 'url'].includes(key) && typeof item === 'string' && item) urls.add(item);
      else if (typeof item === 'object') collect(item);
    });
  };
  collect(bundle);
  const decodeImage = (src) => new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => {
      if (image.decode) image.decode().then(resolve).catch(reject);
      else resolve();
    };
    image.onerror = reject;
    image.src = src;
  });
  const loaded = await Promise.allSettled([...urls].map(async (url) => {
    let objectUrl = null;
    try {
      const response = await window.fetch(url);
      if (!response.ok) throw new Error('Görsel alınamadı.');
      objectUrl = window.URL.createObjectURL(await response.blob());
      await decodeImage(objectUrl);
      return [url, objectUrl];
    } catch (error) {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
      await decodeImage(url);
      return [url, url];
    }
  }));
  if (loaded.some((result) => result.status === 'rejected')) {
    loaded.forEach((result) => {
      if (result.status === 'fulfilled' && result.value[1].startsWith('blob:')) {
        window.URL.revokeObjectURL(result.value[1]);
      }
    });
    throw new Error('Soru görselleri yüklenemedi. Yeniden deneyin.');
  }
  return Object.fromEntries(loaded.map((result) => result.value));
}
